import React, { useCallback, useMemo, useState } from "react";
import { Image, Title, Text, Input, Button, Field } from "components/commons";
import { SoCashlessLogo1 } from "images";
import { useForm, useApi, useRouter } from "hooks";
import { checkProfileEmail, requestForgotPassword } from "apis";
import { Field as FieldType, StyleType } from "enums";
import Validation from "services/validation.service";
import { Path } from "paths";
import lang from "translations";

const initialState = {
  email: {
    name: "email",
    placeholder: lang.enterYourEmailAddress,
    value: "",
    type: FieldType.INPUT,
    validations: [Validation.required(), Validation.emailAddress()],
    maxLength: 75,
  },
};

const ForgotPassword = () => {
  const { history, query } = useRouter();
  const [sentTo, setSentTo] = useState();
  const prefilledEmail = query?.email || "";
  const formState = useMemo(
    () => ({ email: { ...initialState.email, value: prefilledEmail } }),
    [prefilledEmail],
  );
  const { fields, modifyField, applyFieldErrors, submitForm } = useForm({
    initialState: formState,
  });
  const { email } = fields;

  const { request: checkEmailRequest, loading: checkingEmail } = useApi({
    api: checkProfileEmail,
    handleOwnError: true,
  });

  const { request: forgotPasswordRequest, loading: sendingEmail } = useApi({
    api: requestForgotPassword,
    handleOwnError: true,
  });

  const loading = checkingEmail || sendingEmail;

  const handleSubmit = useCallback(async () => {
    const value = email.value.trim();

    let exists;
    try {
      exists = (await checkEmailRequest({ email: value })) !== false;
    } catch (e) {
      exists = false;
    }
    if (!exists) {
      applyFieldErrors({ email: lang.emailNotFoundInSystem });
      return;
    }

    try {
      await forgotPasswordRequest({ email: value });
      setSentTo(value);
    } catch (e) {
      applyFieldErrors({ email: e?.metadata?.message || lang.somethingWentWrong });
    }
  }, [email.value, checkEmailRequest, forgotPasswordRequest, applyFieldErrors]);

  const submitCb = useCallback(() => {
    if (loading) return;
    submitForm(handleSubmit);
  }, [loading, submitForm, handleSubmit]);

  const backToLogin = useCallback(() => history.push(Path.AUTH), [history]);

  return (
    <div className="mt-lg flex flex-col items-center text-center w-11/12 ml-sm sm:w-96">
      <Image className="mb-md" src={SoCashlessLogo1} />
      {sentTo ? (
        <>
          <Title className="mb-lg" xl>
            {lang.checkYourEmail}
          </Title>
          <lang.Translate
            className="text-sm mb-md"
            text={lang.resetPasswordEmailSentTo}
            items={[<span className="font-medium text-sm">{sentTo}</span>]}
          />
          <Button className="mt-md" onClick={backToLogin}>
            {lang.backToLogin}
          </Button>
        </>
      ) : (
        <>
          <Title className="mb-lg" xl>
            {lang.forgotYourPassword}
          </Title>
          <Text className="mb-md">{lang.enterEmailToResetPassword}</Text>
          <Field className="w-full mb-md" message={email.message}>
            <Input center {...email} onChange={modifyField} onEnter={submitCb} />
          </Field>
          <Button onClick={submitCb} disabled={loading}>
            {lang.sendResetLink}
          </Button>
          <Button className="mt-sm text-sm text-gray" type={StyleType.Link} onClick={backToLogin}>
            {lang.backToLogin}
          </Button>
        </>
      )}
    </div>
  );
};

export default ForgotPassword;
